import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Btn, Msg, panelGet, panelPost, useAdminGet } from '../../kit';
import { Note } from '../emailKit';
import { EMAIL_USE_DESIGN_EVENT, pendingDesign, withKeys } from '../designs/types';
import { EMPTY_TAG_FILTER, TagFilterValue, describeTagFilter, useTagOptions } from '../../tags/shared';
import { AudienceMode, AudienceStep, ListState } from './AudienceStep';
import { ContentStep } from './ContentStep';
import { PreviewStep } from './PreviewStep';
import { MissingPerson, PlaceholderFallbacks, PlaceholderReport } from './PlaceholderFallbacks';
import {
  Content, DEFAULT_CAP, EMPTY_CONTENT, EMPTY_EXTRA, Extra, MAX_QUERY, Person, SAMPLE_COUNT, Sender,
  audienceWire, contentProblem, contentWire, describeExtra, downloadPeopleCsv, num,
} from './shared';

/**
 * A new campaign, in four steps: audience → content → preview → send
 * (backend: admin_email_targeting).
 *
 * What goes out is exactly what the operator looked at: the preview is the
 * server's answer for one request (audience + content + envelope); Send is
 * armed only while that request is unchanged, the confirm makes you type the
 * count, and that count travels as expected_count so the server refuses if
 * its own recount differs. Unticked people and hand-picked people travel as
 * ids in the POST, so no list is too long for a URL.
 */

/** A hand-off from the People directory or Leads → Prospects ('utech:email-people'):
 *  the people, optionally a template to preselect and who handed them over. */
export type Handoff = { ids: string[]; seq: number; template?: string; source?: string };

const PEOPLE_CHUNK = 100;   // ids per resolve request (the GET's URL stays short)

export const Composer: React.FC<{
  handoff: Handoff | null;
  sendersVersion: number;
  /** The editable template store (GET email/editable_templates), loaded once by the tab. */
  tplList: { data: any; error: string };
  onSent: (sendId: string) => void;
}> = ({ handoff, sendersVersion, tplList, onSent }) => {
  const opts = useAdminGet('email', 'target_options');
  const sendersQ = useAdminGet('email', 'senders', {}, [sendersVersion]);
  const tagOpts = useTagOptions();
  const o: any = opts.data || {};
  const cap = Number(o.cap || DEFAULT_CAP);
  const senders: Sender[] = (sendersQ.data as any)?.senders || [];
  // Names and triggers from the editable store; "sent" counts from the ledger.
  const templates = useMemo(() => {
    const fromStore: any[] = ((tplList.data as any)?.templates || []).filter((t: any) => !t?.orphan);
    const fromOpts: any[] = o.templates || [];
    const keys = Array.from(new Set([...fromStore.map(t => String(t.key)), ...fromOpts.map(t => String(t.key))]));
    return keys.map(k => {
      const a = fromStore.find(t => String(t.key) === k) || {};
      const b = fromOpts.find(t => String(t.key) === k) || {};
      return { key: k, name: String(a.name || b.name || k), trigger: String(a.trigger || b.trigger || ''),
        sent: Number(b.sent || 0) };
    });
  }, [tplList.data, o.templates]);
  const tplName = (k: string) => templates.find(t => t.key === k)?.name || k;
  const tagLabel = (k: string) => tagOpts.tagByKey[k]?.label || k;

  // -- state ------------------------------------------------------------------
  const [mode, setMode] = useState<AudienceMode>('group');
  const [tf, setTf] = useState<TagFilterValue>(EMPTY_TAG_FILTER);
  const [extra, setExtra] = useState<Extra>(EMPTY_EXTRA);
  const [unticked, setUnticked] = useState<Set<string>>(new Set());
  const [people, setPeople] = useState<Person[]>([]);
  const [content, setContent] = useState<Content>(EMPTY_CONTENT);
  const [skipReceived, setSkipReceived] = useState(true);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState<Note>(null);
  const patchContent = (p: Partial<Content>) => setContent(c => ({ ...c, ...p }));

  // A template until one is picked; a custom mail always has a From.
  useEffect(() => {
    if (!content.key && templates.length) patchContent({ key: templates[0].key });
  }, [templates, content.key]);
  // A design handed over from Email designs or a past campaign ("Use in a
  // campaign" / "Use this design again"): taken on mount or on the event.
  useEffect(() => {
    const take = () => {
      const p = pendingDesign.current;
      if (!p) return;
      pendingDesign.current = null;
      setContent(c => ({ ...c, mode: 'design', design: withKeys(p.doc), design_from: p.name,
        subject: p.subject ?? c.subject }));
    };
    take();
    window.addEventListener(EMAIL_USE_DESIGN_EVENT, take);
    return () => window.removeEventListener(EMAIL_USE_DESIGN_EVENT, take);
  }, []);
  useEffect(() => {
    if (content.mode !== 'template' && !content.from_email && senders.length) {
      patchContent({ from_email: senders[0].email, from_name: senders[0].name });
    }
  }, [content.mode, content.from_email, senders]);

  // -- the People directory hand-off -------------------------------------------
  useEffect(() => {
    if (!handoff || !handoff.ids.length) return;
    const ids = Array.from(new Set(handoff.ids)).slice(0, cap);
    setMode('people');
    setMsg(null);
    if (handoff.template) patchContent({ mode: 'template', key: handoff.template });
    let cancelled = false;
    (async () => {
      setBusy('people');
      const rows: Person[] = [];
      for (let i = 0; i < ids.length; i += PEOPLE_CHUNK) {
        const { data, error } = await panelGet('email', 'target_people_search',
          { user_ids: ids.slice(i, i + PEOPLE_CHUNK).join(',') });
        if (cancelled) return;
        if (error) { setMsg({ kind: 'error', text: `Could not load the people: ${error}` }); break; }
        rows.push(...((data?.rows || []) as Person[]));
      }
      setBusy('');
      setPeople(rows);
      const missing = ids.length - rows.length;
      const dropped = handoff.ids.length - ids.length;
      setMsg({ kind: missing || dropped ? 'error' : 'ok',
        text: `${num(rows.length)} people from ${handoff.source || 'the People directory'} are picked below.`
          + (handoff.template ? ` Template preselected: ${handoff.template} — check it before sending.` : '')
          + (missing > 0 ? ` ${num(missing)} could not be found (deleted accounts?).` : '')
          + (dropped > 0 ? ` Only the first ${num(cap)} were taken — one campaign's cap.` : '') });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handoff?.seq]);

  // -- the live list (group mode) ---------------------------------------------
  const wire = useMemo(() => audienceWire(tf, extra), [tf, extra]);
  const listParams = useMemo(() => {
    const p: Record<string, string> = { filters: JSON.stringify(wire) };
    if (content.mode === 'template' && content.key) {
      p.key = content.key;
      p.skip_already_received = skipReceived ? 'true' : 'false';
    }
    return p;
  }, [wire, content.mode, content.key, skipReceived]);
  const listSig = JSON.stringify(listParams);
  const tooLong = encodeURIComponent(listSig).length > MAX_QUERY;
  const [list, setList] = useState<ListState>({ data: null, loading: false, error: '' });
  const listSeq = useRef(0);
  useEffect(() => {
    if (mode !== 'group' || tooLong) return;
    const t = setTimeout(async () => {
      const mine = ++listSeq.current;
      setList(s => ({ ...s, loading: true, error: '' }));
      const { data, error } = await panelGet('email', 'target_list', listParams);
      if (mine !== listSeq.current) return;
      setList({ data, loading: false, error, sig: data ? listSig : undefined });
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listSig, mode, tooLong]);

  // -- the request a preview and a send both carry ----------------------------
  const listIds = useMemo(() => new Set<string>(((list.data as any)?.rows || []).map((r: any) => String(r.user_id))),
    [list.data]);
  // The list loaded for THESE filters. Until it has (or when its reload
  // failed) every unticked id travels — the server ignores ids that no longer
  // match — so a failed reload can never quietly put unticked people back.
  const listReady = !!list.data && list.sig === listSig;
  // With the list: only ids on it (someone unticked under an earlier filter
  // who no longer matches is simply not in the audience).
  const excludeIds = useMemo(() => (listReady ? Array.from(unticked).filter(id => listIds.has(id)) : Array.from(unticked)),
    [unticked, listIds, listReady]);
  // A list too long to load (a long pasted list) never becomes ready: every
  // unticked id travels then, and the server ignores ones that do not match.
  const waitingForList = mode === 'group' && unticked.size > 0 && !listReady && !tooLong;
  const filters = useMemo(() => {
    if (mode === 'people') {
      // Picked one by one: the team is not left out behind the admin's back.
      return { user_ids: people.map(p => p.user_id), team: 'include' };
    }
    return excludeIds.length ? { ...wire, exclude_user_ids: excludeIds } : wire;
  }, [mode, people, excludeIds, wire]);
  const request = useMemo(() => ({ filters, ...contentWire(content, skipReceived) }),
    [filters, content, skipReceived]);
  // The campaign's name is a label on the record, not part of who gets what,
  // so naming it after the preview does not make the preview stale.
  const signature = JSON.stringify({ ...request, name: '' });

  const maxCc = Number(o.max_cc || 10);
  const problem = contentProblem(content, maxCc);
  const audienceEmpty = mode === 'people' ? people.length === 0 : false;

  // -- placeholders: what each becomes, and who is missing a value (live) -------
  // Checked against the same audience the preview would use, a moment after
  // the content or the audience last changed, so the warning is there before
  // anyone presses Preview. No audience yet (nobody picked by hand): the
  // placeholders alone — an empty user_ids would mean everyone.
  const phReady = content.mode === 'template' ? !!content.key : content.mode === 'design' ? !!content.design : true;
  const phBody = useMemo(() => {
    const b: Record<string, any> = contentWire(content, skipReceived);
    if (!audienceEmpty) b.filters = filters;
    return b;
  }, [content, skipReceived, filters, audienceEmpty]);
  const phSig = JSON.stringify({ ...phBody, name: '' });
  const [ph, setPh] = useState<{ data: PlaceholderReport | null; loading: boolean; error: string; sig?: string }>(
    { data: null, loading: false, error: '' });
  const phSeq = useRef(0);
  useEffect(() => {
    if (!phReady) { phSeq.current++; setPh({ data: null, loading: false, error: '' }); return; }
    const t = setTimeout(async () => {
      const mine = ++phSeq.current;
      setPh(s => ({ ...s, loading: true }));
      const { data, error } = await panelPost('email', 'target_placeholders', phBody);
      if (mine !== phSeq.current) return;
      // A refused value (a link that is not one) keeps the last report on
      // screen, so the field being typed in does not vanish under the error.
      setPh(s => ({ data: error ? s.data : (data as PlaceholderReport), loading: false, error, sig: error ? s.sig : phSig }));
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phSig, phReady]);
  const setFallback = (n: string, v: string | null) => setContent(c => {
    const fallbacks = { ...c.fallbacks };
    if (v === null) delete fallbacks[n]; else fallbacks[n] = v;
    return { ...c, fallbacks };
  });
  const setOverride = (n: string, v: string | null) => setContent(c => {
    const overrides = { ...c.overrides };
    if (v === null) delete overrides[n]; else overrides[n] = v;
    return { ...c, overrides };
  });
  // The report lists the first 500 people per placeholder; the CSV asks for
  // all of them.
  const missingCsv = async (n: string, rows: MissingPerson[], missing: number) => {
    let all = rows;
    if (rows.length < missing) {
      const { data, error } = await panelPost('email', 'target_placeholders', { ...phBody, all_people: 1 });
      if (error) { setMsg({ kind: 'error', text: `Could not list everyone missing {${n}}: ${error}` }); return; }
      all = ((data as PlaceholderReport)?.placeholders || []).find(r => r.name === n)?.people || rows;
    }
    downloadPeopleCsv(`missing_${n}`, all.map(r => ({ user_id: r.user_id, email: r.email, name: r.name })));
  };
  const leaveOut = (ids: string[]) => {
    if (!ids.length) return;
    if (mode === 'people') setPeople(p => p.filter(x => !ids.includes(x.user_id)));
    else setUnticked(s => new Set([...Array.from(s), ...ids]));
  };
  const placeholdersPanel = phReady ? (
    <PlaceholderFallbacks report={ph.data} loading={ph.loading} error={ph.error}
      updating={!!ph.data && ph.sig !== phSig}
      fallbacks={content.fallbacks} overrides={content.overrides}
      onFallback={setFallback} onOverride={setOverride} onLeaveOut={leaveOut}
      onCsv={missingCsv} />
  ) : null;

  // -- preview ----------------------------------------------------------------
  const [preview, setPreview] = useState<any>(null);
  const [previewSig, setPreviewSig] = useState('');
  /** previewEmail: render that recipient first (a check on one person). */
  const runPreview = async (previewEmail = '') => {
    if (problem || audienceEmpty) return;
    setBusy('preview'); setMsg(null);
    const sigNow = signature;
    const body: Record<string, any> = { ...request, sample_count: SAMPLE_COUNT };
    if (previewEmail) body.preview_email = previewEmail;
    const { data, error } = await panelPost('email', 'target_preview', body);
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: error }); setPreview(null); setPreviewSig(''); return; }
    setPreview(data); setPreviewSig(sigNow);
    if (previewEmail && String((data as any)?.samples?.[0]?.to || '').toLowerCase() !== previewEmail.toLowerCase()) {
      setMsg({ kind: 'error', text: `${previewEmail} is not among this campaign's recipients.` });
    }
  };
  const stale = !!preview && previewSig !== signature;
  const count = Number(preview?.count ?? 0);
  const overCap = !!preview?.over_cap || count > cap;
  const unknown: string[] = preview?.placeholders_unknown || [];
  const blocker = !preview ? '' : busy !== '' ? 'Working…'
    : count === 0 ? 'Nobody to send to.'
      : overCap ? `Over the ${num(cap)} cap.`
        : unknown.length ? 'Fix the placeholders we cannot fill (above).'
          : !content.name.trim() ? 'Name the campaign (step 2) before sending.' : '';

  // -- send -------------------------------------------------------------------
  const post = (dry: boolean) => panelPost('email', 'target_send', {
    ...request, expected_count: count, dry_run: dry, confirm: !dry,
  });
  const dryRun = async () => {
    setBusy('dry'); setMsg(null);
    const { data, error } = await post(true);
    setBusy('');
    const d: any = data;
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    setMsg({
      kind: d?.sender_ready === false ? 'error' : 'ok',
      text: `Dry run passed — nothing was sent or written. ${num(d?.count)} recipient(s) would get “${d?.subject || ''}”.`
        + (d?.sender_ready === false ? ' But the sender is NOT ready on this server (no SendGrid key or '
          + 'shared sender), so a real send would be refused.' : ''),
    });
  };
  const send = async () => {
    setBusy('send'); setMsg(null);
    const { data, error } = await post(false);
    setBusy('');
    const d: any = data;
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    const eta = Number(d?.eta_sec || 0);
    setMsg({
      kind: 'ok',
      text: `Sending “${d?.name || d?.subject || ''}” to ${num(d?.queued ?? count)} people`
        + (eta ? ` — about ${eta < 90 ? `${eta} s` : `${Math.round(eta / 60)} min`}` : '')
        + `. Campaign ${d?.send_id || ''}; its progress and funnel are under Campaigns.`,
    });
    setPreview(null); setPreviewSig(''); setUnticked(new Set());
    if (d?.send_id) onSent(String(d.send_id));
  };

  const clear = () => {
    setTf(EMPTY_TAG_FILTER); setExtra(EMPTY_EXTRA); setUnticked(new Set()); setPeople([]);
  };
  const summary = mode === 'people'
    ? [`${num(people.length)} people picked by hand (team members included if picked)`]
    : [...describeTagFilter(tf, tagLabel), ...describeExtra(extra, tplName),
       ...(excludeIds.length ? [`${num(excludeIds.length)} unticked by hand`] : [])];
  const recipients = preview ? count : mode === 'people' ? people.length
    : (list.data ? Number((list.data as any).count || 0) - excludeIds.length : null);

  return (
    <div className="flex flex-col gap-4">
      {(opts.error || tplList.error) && <Msg kind="error">{opts.error || tplList.error}</Msg>}
      <AudienceStep mode={mode} onMode={setMode} tf={tf} onTf={setTf}
        extra={extra} onExtra={p => setExtra(x => ({ ...x, ...p }))}
        templates={templates} countries={o.countries || []}
        totals={o.total_users !== undefined ? { users: o.total_users, team: o.team_members, bounced: o.bounced_users } : undefined}
        list={list} tooLong={tooLong} unticked={unticked} onUnticked={setUnticked}
        people={people} onPeople={setPeople} cap={cap} onClear={clear} />
      <ContentStep content={content} onContent={patchContent} templates={templates}
        senders={senders} sendersError={sendersQ.error}
        placeholders={o.custom_placeholders || []}
        skipReceived={skipReceived} onSkip={setSkipReceived}
        recipients={recipients} problem={problem} placeholdersPanel={placeholdersPanel} />
      <div className="flex items-center gap-3 flex-wrap">
        <Btn kind="primary" onClick={() => runPreview()} disabled={busy !== '' || !!problem || audienceEmpty || waitingForList}>
          {busy === 'preview' ? 'Rendering…' : preview && !stale ? 'Preview again' : 'Preview the e-mail and the count'}
        </Btn>
        <span className="text-xs text-grayscale-1100">
          {audienceEmpty ? 'Pick at least one person first.'
            : waitingForList ? 'Waiting for the list for these filters (people are unticked on it) — refresh if it failed.'
            : summary.join(' · ')}
        </span>
      </div>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      {preview && (
        <PreviewStep preview={preview} stale={stale} busy={busy} cap={cap} content={content}
          subjectFallback={content.mode === 'template' ? tplName(content.key) : content.subject}
          summary={summary} blocker={blocker} onSend={send} onDryRun={dryRun} onRenderFor={runPreview} />
      )}
    </div>
  );
};
